import { Location } from "@angular/common";
import { AfterViewInit, Component, ElementRef, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";

import {
  BaseComponent,
  ContentService,
  environment,
  EventBusService,
  HelperService,
  Lesson,
  ProductService,
  StorageService,
  StorageKeys,
  UserService,
  Video,
  EventsService,
} from "@mypxplat/xplat/core";
import { AppService, AvoService, BetaService, WebCommunityService } from "@mypxplat/xplat/web/core";
import { filter, take, takeUntil } from "rxjs/operators";
import { FuiContentFilterComponent } from "../fender-ui";
import { BreadcrumbItem } from "../fender-ui/layout-header/layout-header.component";
import { ClipboardService } from "ngx-clipboard";
import { FilterableVideoGroupComponent } from "../filterable-video-group/filterable-video-group.component";
import { TranslateService } from "@ngx-translate/core";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "fui-learn",
  templateUrl: "fui-learn.component.html",
  standalone: false,
})
export class FuiLearnComponent extends BaseComponent implements AfterViewInit {
  public breadcrumbs: BreadcrumbItem[] = [];
  public env = environment;
  public displayedSection: string;
  public displayedFolder: any;
  public displayedVideoGroup: {
    videos: Array<Video>;
    title: string;
    description?: string;
    ignoreBrand?: boolean;
    translationKey?: string;
  };
  public displayedTag: any;
  public displayedProduct: any;
  public refreshing: boolean = false;
  public myList: Array<any> = [];
  public savedVideos: Array<Video> = [];
  public inProgressVideos: Array<Video> = [];
  public educationData: any;
  public featuredLessons: Array<Lesson>;
  public featuredVideos: Array<Video>;
  public myProductsWithVideos: Array<any>;
  public routerEvents: any;
  public selectedCourseIndex: number = 0;
  public filters: any = {
    sort_by: "date",
    sort_order: "desc",
    product: "any",
    tag: "any",
    duration: "any",
    level: "any",
    query: "",
  };

  public filteredVideos: Array<Video>;
  public displayedVideos: Array<Video>;
  public displayedFolders: Array<any>;
  public displayedLessons: Array<any>;
  public limit: number = 20;
  public page: number = 0;
  public totalPages: number;
  public totalVideos: number;
  public lessonLoadingMap: any = {};
  public lessonCopiedMap: any = {};

  @ViewChild("fuiContentFilter") fuiContentFilter: FuiContentFilterComponent;
  @ViewChild("fuiSidebarFilter") fuiSidebarFilter: ElementRef;
  @ViewChild("sidebarFilterModal") sidebarFilterModal: any;
  @ViewChild("learnNavPills") learnNavPills: ElementRef<HTMLUListElement>;
  public learnPillsScrollable: boolean = false;
  public learnPillsAtEnd: boolean = true;
  @ViewChild("filterableVideoGroup") filterableVideoGroup: FilterableVideoGroupComponent;
  @ViewChild("featuredVideoIframe") featuredVideoIframe?: ElementRef<HTMLIFrameElement>;
  @ViewChild("featuredVideoContainer") featuredVideoContainer?: ElementRef<HTMLElement>;

  public featuredFenderVideoId: number = environment.featuredFenderVideoId;
  public featuredFenderStudioVideoId: number = environment.featuredFenderStudioVideoId;
  public featuredVideoId: number = this.appService.videoContentType.presonus ? this.featuredFenderStudioVideoId : this.featuredFenderVideoId;
  public featuredVideo: Video;
  public featuredVideoEmbedUrl: SafeResourceUrl | null = null;
  private _featuredVideoPaused: boolean = false;
  private _featuredVideoPausedManually: boolean = false;
  private _featuredVideoPausedByScroll: boolean = false;
  private _featuredVideoPausedByExternal: boolean = false;
  private _featuredVideoInView: boolean = true;
  private _featuredVideoIntersectionObserver?: IntersectionObserver;
  public videosInitted: boolean = false;
  constructor(
    userService: UserService,
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _storage: StorageService,
    public appService: AppService,
    public betaService: BetaService,
    public communityService: WebCommunityService,
    public contentService: ContentService,
    public eventBusService: EventBusService,
    public helperService: HelperService,
    public location: Location,
    public productService: ProductService,
    public translate: TranslateService,
    private _clipboardService: ClipboardService,
    private _sanitizer: DomSanitizer,
    private avoService: AvoService,
    public eventsService: EventsService
  ) {
    super(userService);
  }

  ngOnInit() {
    this.breadcrumbs = [{ label: "header.nav.home", routerLink: ["/home"] }, { label: "fui-header.learn" }];
    this.init();

    // Pause the hero video whenever any video playback starts elsewhere.
    this.eventBusService
      .observe(this.eventBusService.types.playVideo)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.pauseFeaturedVideo("external"));

    // Resume the hero video when the global player closes.
    this.eventBusService
      .observe(this.eventBusService.types.videoClosed)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.resumeFeaturedVideo("external"));

    this.routerEvents = this._router.events.subscribe((result) => {
      // this allows you to "navigate" to another video list. Used for when users click a tag in a video grid.
      if (result instanceof NavigationEnd) {
        if (result.url.indexOf("/learn") === 0) {
          this.syncLearnTitleFromRoute();
        }
        if (result.url.indexOf("learn/videos") > -1) this.init(true);
      }
    });

    this.appService.viewportChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateLearnPillsScrollState();
    });
  }

  ngAfterViewInit() {
    this.updateLearnPillsScrollState();
    // Re-measure after fonts/layout settle so the scroll affordance is accurate
    setTimeout(() => this.updateLearnPillsScrollState(), 100);
    // Auto-pause the hero when it scrolls out of view.
    if (typeof IntersectionObserver === "undefined") return;
    const el = this.featuredVideoContainer?.nativeElement;
    if (!el) return;

    this._featuredVideoIntersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        const inView = entry.isIntersecting && entry.intersectionRatio >= 0.35;
        this._featuredVideoInView = inView;
        if (!inView) {
          this.pauseFeaturedVideo("scroll");
        } else {
          this.resumeFeaturedVideo("scroll");
        }
      },
      { threshold: [0, 0.15, 0.35, 0.5, 0.75, 1] }
    );

    this._featuredVideoIntersectionObserver.observe(el);
  }

  onLearnPillsScroll(): void {
    this.updateLearnPillsScrollState();
  }

  updateLearnPillsScrollState(): void {
    const ul = this.learnNavPills?.nativeElement;
    if (!ul) return;
    const maxScroll = ul.scrollWidth - ul.clientWidth;
    this.learnPillsScrollable = maxScroll > 2;
    this.learnPillsAtEnd = !this.learnPillsScrollable || ul.scrollLeft >= maxScroll - 2;
  }

  selectFolder(folder) {
    this.appService.storedBodyPosition = document.body.scrollTop || document.documentElement.scrollTop;
    this.displayedFolder = folder;
    this.displayedVideoGroup = {
      videos: [...folder.videos],
      title: folder.title,
      description: folder.description,
      ignoreBrand: true,
    };
    const route = "learn/videos?folder=" + this.displayedFolder.string_id;
    this.location.replaceState(route);
    this.recordLearnNavigation(route);
    this.appService.scrollBodyTo(0);

    // Fire Loaded a Page for folder view (only in My Fender)
    if (this.userService.appId === "fender") {
      this.avoService.avoInitted$
        .pipe(
          filter((v) => !!v),
          take(1),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          const te = this.avoService.trackEvent();
          if (te && typeof te.loadedAPage === "function") {
            const pageName = folder?.title || "Video Folder";
            const page = this.avoService.getPageProperties({ pageName });
            const payload: any = {
              ...page,
              customDestinationPageName_: pageName,
            };
            te.loadedAPage(payload);
          }
        });
    }
  }

  showAllVideos(focusInput: boolean = false) {
    this.appService.storedBodyPosition = document.body.scrollTop || document.documentElement.scrollTop;
    this.displayedSection = undefined;
    this.displayedVideoGroup = {
      videos: [...this.educationData.videos],
      title: "All Videos",
      description: "Search our entire library of videos.",
    };
    const route = "learn/videos/search";
    this.location.replaceState(route);
    this.recordLearnNavigation(route);
    this.syncLearnTitleFromRoute("videos", "search");
    this.appService.scrollBodyTo(0);

    if (focusInput) {
      setTimeout(() => {
        const input = document.getElementById("content-filter-search");
        if (input) input.focus();
      }, 100);
    }
  }

  goBack() {
    if (this.displayedSection == "mylist") {
      this.displayedVideoGroup = undefined;
      setTimeout(() => {
        this.appService.scrollBodyTo(this.appService.storedBodyPosition);
      }, 100);
    } else {
      this.displayedFolder = undefined;
      this.displayedVideoGroup = undefined;
      this.displayedSection = "videos";
      this.filterableVideoGroup.clearFilter();
      let routeTo = "learn/videos";
      this.location.replaceState(routeTo);
      this.recordLearnNavigation(routeTo);
      this.syncLearnTitleFromRoute("videos");
      setTimeout(() => {
        this.appService.scrollBodyTo(this.appService.storedBodyPosition);
      }, 100);
    }
  }

  toggleVideoContentType(brand) {
    let existing = this.appService.videoContentType;
    existing[brand] = !existing[brand];
    this.appService.videoContentType = existing;
    if (this.displayedSection == "courses" && !this.appService.videoContentType.presonus) {
      this.switchSection("videos");
    }
    if (this.filterableVideoGroup) this.filterableVideoGroup.videoContentTypeChanged();
    this.videoContentTypeChanged();
  }

  public playFolder: any;

  init(force?) {
    this.displayedSection = this._activatedRoute.snapshot.params["category"];
    this.syncLearnTitleFromRoute(this.displayedSection, this._activatedRoute.snapshot.params["id"]);

    this.contentService.educationDataStream$
      .pipe(
        takeUntil(this.destroy$),
        filter((item) => !!item)
      )
      .subscribe((result) => {
        if (this.videosInitted && !force) return;
        this.videosInitted = true;
        this.educationData = result;
        this.educationData.videos.sort((a, b) => (a.created > b.created ? -1 : 1));
        this.featuredLessons = [...this.contentService.lessons.filter((item, index) => item.status != "deleted" && item.status != "pending" && index < 5)];
        this.featuredLessons.sort((a, b) => a.order - b.order);
        this.educationData.lessons = [...this.educationData.lessons.filter((item) => item.status != "deleted" && item.status != "pending")];
        this.educationData.lessons.sort((a, b) => a.order - b.order);
        this.educationData.courses.sort((a, b) => a.order - b.order);

        let productStringId = this._activatedRoute.snapshot.queryParams["product"];
        let folderStringId = this._activatedRoute.snapshot.queryParams["folder"];
        let tagStringId = this._activatedRoute.snapshot.queryParams["tag"];
        if (productStringId || folderStringId || tagStringId) {
          if (folderStringId) {
            this.chooseFolder(this.educationData.folders.filter((item) => item.string_id == folderStringId)[0]);
          } else if (tagStringId) {
            this.chooseTag(this.contentService.sidebarTags.filter((item) => item.string_id == tagStringId)[0]);
          }
        }

        const applyProductAwareView = () => {
          if ((this.appService.videoContentType.presonus && this.appService.videoContentType.fender) || (!this.appService.videoContentType.presonus && !this.appService.videoContentType.fender)) {
            // all
            this.featuredVideos = this.educationData.videos.filter((item, index) => index < 8);
          } else {
            // fender or presonus
            const selectedBrand = this.appService.videoContentType.fender ? "fender" : "presonus";
            this.featuredVideos = this.educationData.videos.filter((item) => item.brand == selectedBrand || item.brand == "both").filter((item, index) => index < 8);
          }
          this.playFolder = this.educationData.folders.filter((item) => item.title == "Introduction to Fender Play")[0];
          this.myProductsWithVideos = [];
          this.contentService.productsAssociatedWithVideos.forEach((item) => {
            const catalogEntry = this.productService.productsByProductID[item.id];
            if (catalogEntry) this.myProductsWithVideos.push(catalogEntry);
          });
          if (productStringId) {
            const catalogProduct = this.productService.productsByStringIDs[productStringId];
            const videoProduct = catalogProduct ? this.contentService.productsAssociatedWithVideos.find((item: any) => item.id == catalogProduct.id) : undefined;
            if (videoProduct) this.chooseProduct({ ...videoProduct, stringId: catalogProduct.stringId });
          }
          this.initFeaturedVideo();
          this.videoContentTypeChanged();
          if (this.filterableVideoGroup) this.filterableVideoGroup.videoContentTypeChanged();
        };

        if (this.user) {
          this.productService.products$
            .pipe(
              filter((result) => !!result),
              takeUntil(this.destroy$)
            )
            .subscribe(() => applyProductAwareView());
        } else {
          applyProductAwareView();
        }
      });
    if (!this.educationData) this.refresh();
  }

  initFeaturedVideo() {
    const processLivestream = () => {
      const candidates = this.eventsService.cachedAllEvents.filter((e) => this.eventsService.isLivestreamEvent(e));
      const livestreamEvent = this.pickRelevantLivestreamEvent(candidates);
      const platform = livestreamEvent ? this.eventsService.getLivestreamPlatform(livestreamEvent) : null;
      const videoId = livestreamEvent ? this.eventsService.getLivestreamVideoId(livestreamEvent) : null;
      if (livestreamEvent && platform && videoId) {
        const tz = livestreamEvent.timezone || "America/Chicago";
        const startTime = this.parseLivestreamDatetime(livestreamEvent.start_datetime, tz).getTime();
        const endTime = livestreamEvent.end_datetime ? this.parseLivestreamDatetime(livestreamEvent.end_datetime, tz).getTime() : startTime + 3600000;
        const now = Date.now();

        if (now >= startTime && now <= endTime) {
          // Muted autoplay works for both platforms; the query strings are platform-specific.
          // YouTube: mute + autoplay + minimal chrome. Vimeo: keep the existing app_id (registered
          // for this domain in Vimeo's referrer allowlist).
          const url =
            platform === "youtube"
              ? `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&modestbranding=1&rel=0`
              : `https://player.vimeo.com/video/${videoId}?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=173584`;
          this.featuredVideoIsLivestream = true;
          this.featuredVideoMuted = true;
          this.featuredVideoId = 1;
          this.featuredVideoEmbedUrl = this._sanitizer.bypassSecurityTrustResourceUrl(url);
          this.featuredVideo = {
            // Video.id is typed as number, but this synthetic featured-video entry only exists
            // to drive the on-page display — nothing keys off it. Cast to any to keep the shape
            // consistent with the previous code (which passed the raw string) without widening
            // Video.id upstream.
            id: videoId as any,
            platform,
            platform_id: videoId,
            created: new Date().toISOString(),
            duration: 0,
            status: "active",
            title: this.translate.instant("home.happening_now_label") + livestreamEvent.title,
            snippet: "",
          };
          // Vimeo needs a postMessage to actually go muted; YouTube's `mute=1` param handles it.
          if (platform === "vimeo") setTimeout(() => this.postToFeaturedVimeoPlayer("setVolume", 0), 2000);
          return;
        } else {
          this.featuredVideoIsLivestream = false;
          this.featuredVideo = this.educationData.videos.filter((item) => item.id == this.featuredVideoId)[0];
          this.featuredVideoEmbedUrl = this.featuredVideo ? this.buildYoutubeEmbedUrl(this.featuredVideo.platform_id) : null;
        }

        if (now < startTime) {
          this.heroCountdownEvent = livestreamEvent;
          this.featuredVideoId = 1;
          this.featuredVideoEmbedUrl = null;
          this.featuredVideo = null;
          this.startHeroCountdown(startTime);
          return;
        }
      } else if (this.featuredVideoId) {
        this.featuredVideoIsLivestream = false;
        this.featuredVideo = this.educationData.videos.filter((item) => item.id == this.featuredVideoId)[0];
        this.featuredVideoEmbedUrl = this.featuredVideo ? this.buildYoutubeEmbedUrl(this.featuredVideo.platform_id) : null;
      }
    };

    if (this.eventsService.cachedAllEvents.length) {
      processLivestream();
    } else {
      this.eventsService.getPublicEvents(50).subscribe({ next: () => processLivestream() });
    }
  }

  private pickRelevantLivestreamEvent(events: any[]): any {
    if (!events.length) return null;
    const now = Date.now();
    const twelveHoursFromNow = now + 12 * 60 * 60 * 1000;
    for (const e of events) {
      const tz = e.timezone || "America/Chicago";
      const startTime = this.parseLivestreamDatetime(e.start_datetime, tz).getTime();
      const endTime = this.parseLivestreamDatetime(e.end_datetime || e.start_datetime, tz).getTime();
      if (endTime > now && startTime <= twelveHoursFromNow) return e;
    }
    return null;
  }

  private parseLivestreamDatetime(dt: string, timezone: string): Date {
    const parts = dt.split("T");
    const [y, mo, d] = parts[0].split("-").map(Number);
    const [h, mi] = (parts[1] || "00:00").split(":").map(Number);
    const fake = new Date(y!, mo! - 1, d!, h!, mi!);
    const inTz = new Date(fake.toLocaleString("en-US", { timeZone: timezone }));
    const offset = fake.getTime() - inTz.getTime();
    return new Date(fake.getTime() + offset);
  }

  private startHeroCountdown(startTime: number) {
    this.heroCountdownInterval = setInterval(() => {
      if (Date.now() >= startTime) {
        clearInterval(this.heroCountdownInterval);
        this.heroCountdownEvent = null;
        this.initFeaturedVideo();
      }
    }, 5000);
  }

  showFeaturedVideo(id) {
    if (this.featuredVideoIsLivestream) {
      // Only Vimeo's embed iframe needs a postMessage to go muted; YouTube's `mute=1` query
      // param already handled it. Calling into the vimeo postMessage helper against a YT iframe
      // is harmless (no matching origin) but skipping is cleaner.
      if (this.featuredVideo?.platform === "vimeo") {
        this.postToFeaturedVimeoPlayer("setVolume", 0);
      }
      this.featuredVideoMuted = true;
    }
    let video = this.contentService.videosByPlatformId[id];
    if (video) {
      this.eventBusService.emit(this.eventBusService.types.playVideo, {
        id: video.platform_id,
        title: video.title,
        type: video.platform,
        thumb: video.image,
        presonus_id: video.id,
        presonusVideo: video,
      });
    } else {
      // Fallback path: no Learn entry for this id, so it's a livestream that isn't in the
      // videos catalog. Read the platform from the currently-featured video (which is what
      // triggered this click) instead of defaulting to vimeo.
      this.eventBusService.emit(this.eventBusService.types.playVideo, {
        id: id,
        type: this.featuredVideo?.platform === "youtube" ? "youtube" : "vimeo",
      });
    }
  }

  refresh() {
    this.refreshing = true;
    this.contentService.getEducationData(false, true).subscribe((result) => (this.refreshing = false));
  }

  public showFilterOptions: boolean = true;
  closeFilterOptions() {
    this.showFilterOptions = false;
  }

  clearFilter(filter?) {
    if (filter == "product") {
      this.filters.product = "any";
    } else if (filter == "tag") {
      this.filters.tag = "any";
    } else if (filter == "duration") {
      this.filters.duration = "any";
    } else if (filter == "level") {
      this.filters.level = "any";
    } else if (!filter) {
      this.filters.query = "";
      this.filters.product = "any";
      this.filters.tag = "any";
      this.filters.duration = "any";
      this.filters.level = "any";
    }
  }

  showMoreVideos(videos, title?: string, description?: string, translated?: boolean) {
    // Fire CTA Clicked for Show More Video (only in My Fender)
    if (this.userService.appId === "fender") {
      this.avoService.avoInitted$
        .pipe(
          filter((v) => !!v),
          take(1)
        )
        .subscribe(() => {
          const te = this.avoService.trackEvent();
          if (te && typeof (te as any).ctaClicked === "function") {
            const page = this.avoService.getPageProperties({ pageName: "Learn" });
            const extra: Record<string, any> = {
              ctaText: "Show More",
              ctaLocation: "Learn",
              utmId: (page as any).utmId ?? 0,
              utmTerm: (page as any).utmTerm ?? "",
            };
            const payload = this.avoService.buildEventPayload<Record<string, any>>(page, extra, { omitNulls: true });
            (te as any).ctaClicked(payload);
          }
        });
    }
    this.appService.storedBodyPosition = document.body.scrollTop || document.documentElement.scrollTop;
    this.displayedVideoGroup = {
      videos: [...videos],
      title: translated && title ? this.translate.instant(title) : title,
      description: translated && description ? this.translate.instant(description) : description,
      translationKey: translated ? title : undefined,
    };
    this.appService.scrollBodyTo(0);
  }

  filter() {
    this.displayedFolders = this.educationData?.folders ? [...this.educationData.folders].sort((a, b) => a.order - b.order) : [];
    this.displayedLessons = this.educationData?.lessons ? [...this.educationData.lessons].sort((a, b) => a.order - b.order) : [];
    if ((this.appService.videoContentType.fender && this.appService.videoContentType.presonus) || (!this.appService.videoContentType.fender && !this.appService.videoContentType.presonus)) {
      this.displayedFolders = [...this.educationData.folders].sort((a, b) => a.order - b.order);
      this.displayedLessons = [...this.educationData.lessons].sort((a, b) => a.order - b.order);
    } else {
      const selectedBrand = this.appService.videoContentType.fender ? "fender" : "presonus";
      this.displayedFolders = [...this.educationData.folders.filter((item) => item.brand == selectedBrand || item.brand == "both")].sort((a, b) => a.order - b.order);
      this.displayedLessons = [...this.educationData.lessons.filter((item) => item.brand == selectedBrand || item.brand == "both")].sort((a, b) => a.order - b.order);
    }
  }

  videoContentTypeChanged() {
    this.filter();
  }

  switchSection(section) {
    this.displayedVideoGroup = undefined;
    if (section != this.displayedSection) {
      this.displayedSection = section;
      this._router.navigate(["/learn", section], { replaceUrl: true }).then(() => {
        this.syncLearnTitleFromRoute(section);
      });
    }
  }

  private syncLearnTitleFromRoute(categoryOverride?: string, idOverride?: string) {
    // Title/SEO is owned by the route's page component (EducationPageComponent) via SeoService.
    // Leaving this method as a no-op keeps existing call sites intact.
  }

  private resolveLearnSectionTitle(category: string, id: string): string {
    const base = "Learn";

    if (category === "videos" && id === "search") {
      return base + " - Search";
    }
    if (category === "lessons") {
      return base + " - Lessons";
    }
    if (category === "mylist") {
      return base + " - My Library";
    }
    if (category === "videos") {
      return base + " - Videos";
    }

    return base;
  }

  private recordLearnNavigation(route: string): void {
    const navPath = route.startsWith("/") ? route : "/" + route;
    this.avoService.recordNavigation(navPath);
  }

  viewLesson(lesson) {
    this._router.navigate(["/learn/lesson", lesson.id]);
  }

  chooseFolder(folder) {
    if (!folder) return;
    this.displayedTag = undefined;
    this.filters.tag = "any";
    if (this.displayedSection != "videos") this.switchSection("videos");
    this.displayedFolder = folder;
    this.displayedVideos = this.displayedFolder.videos;
    const route = "learn/videos?folder=" + this.displayedFolder.string_id;
    this.location.replaceState(route);
    this.recordLearnNavigation(route);
    this.displayedVideoGroup = {
      videos: [...this.displayedFolder.videos],
      title: this.displayedFolder.title,
      description: this.displayedFolder.description,
      ignoreBrand: true,
    };
  }

  chooseTag(tag) {
    if (!tag) return;
    this.showAllVideos();
    setTimeout(() => {
      if (this.filterableVideoGroup) this.filterableVideoGroup.chooseTag(tag);
    }, 100);
    const route = "learn/videos?tag=" + tag.string_id;
    this.location.replaceState(route);
    this.recordLearnNavigation(route);
  }

  chooseProduct(product) {
    if (!product) return;
    this.showAllVideos();
    setTimeout(() => {
      if (this.filterableVideoGroup) this.filterableVideoGroup.chooseProduct(product);
    }, 100);
  }

  toggleSaveLesson(lesson) {
    if (!this.user) {
      this.appService.redirectToConnect(true, false);
      return;
    }
    this.lessonLoadingMap[lesson.id] = true;
    let existingData = this.contentService.userLearnData[lesson.type == "lesson" ? "lessons" : "courses"][lesson.id];
    let saved = existingData?.enrolled ? false : true;
    let args: any = {
      user_id: this.user.id,
      learn_group_id: lesson.id,
      enrolled: saved,
    };
    if (!existingData?.date_started) args.date_started = new Date();

    this.contentService.saveUserLearnGroupData(lesson.id, args).subscribe({
      next: (result) => {
        this.lessonLoadingMap[lesson.id] = false;
      },
      error: (error) => {
        this.appService.alertError(error);
        this.lessonLoadingMap[lesson.id] = false;
      },
    });
  }

  copyLink(lesson) {
    this.lessonCopiedMap[lesson.id] = true;
    this._clipboardService.copy(environment.fenderUrl + "learn/" + lesson.type + "/" + lesson.id);
    event.stopPropagation();
    setTimeout(() => {
      this.lessonCopiedMap[lesson.id] = false;
    }, 1000);
  }

  ngOnDestroy() {
    if (this.routerEvents && this.routerEvents.unsubscribe) this.routerEvents.unsubscribe();
    if (this._featuredVideoIntersectionObserver) this._featuredVideoIntersectionObserver.disconnect();
    if (this.heroCountdownInterval) clearInterval(this.heroCountdownInterval);
    super.ngOnDestroy();
  }

  pauseFeaturedVideo(reason: "manual" | "scroll" | "external" = "manual") {
    if (reason === "manual") this._featuredVideoPausedManually = true;
    if (reason === "scroll") this._featuredVideoPausedByScroll = true;
    if (reason === "external") this._featuredVideoPausedByExternal = true;

    this._featuredVideoPaused = true;
    if (this.featuredVideoIsLivestream) {
      this.postToFeaturedVimeoPlayer("pause");
    } else {
      this.postToFeaturedYoutubePlayer("pauseVideo");
    }
  }

  resumeFeaturedVideo(reason: "scroll" | "external" = "external") {
    // Only auto-resume when it's safe to do so.
    if (this._featuredVideoPausedManually) return;
    if (!this._featuredVideoInView) return;

    if (reason === "scroll") this._featuredVideoPausedByScroll = false;
    if (reason === "external") this._featuredVideoPausedByExternal = false;

    if (this._featuredVideoPausedByScroll || this._featuredVideoPausedByExternal) return;

    this._featuredVideoPaused = false;
    if (this.featuredVideoIsLivestream) {
      this.postToFeaturedVimeoPlayer("play");
    } else {
      this.postToFeaturedYoutubePlayer("playVideo");
    }
  }

  // The embed uses mute=1, so start muted.
  public featuredVideoMuted: boolean = true;
  public featuredVideoIsLivestream: boolean = false;
  public heroCountdownEvent: any = null;
  private heroCountdownInterval: any;
  toggleMuteFeaturedVideo() {
    this.featuredVideoMuted = !this.featuredVideoMuted;
    if (this.featuredVideoIsLivestream) {
      this.postToFeaturedVimeoPlayer(this.featuredVideoMuted ? "setVolume" : "setVolume", this.featuredVideoMuted ? 0 : 1);
    } else {
      this.postToFeaturedYoutubePlayer(this.featuredVideoMuted ? "mute" : "unMute");
    }
  }

  private postToFeaturedYoutubePlayer(func: "pauseVideo" | "playVideo" | "mute" | "unMute") {
    const iframe = this.featuredVideoIframe?.nativeElement;
    if (!iframe) return;

    // YouTube IFrame API listens for postMessage commands when enablejsapi=1.
    try {
      iframe.contentWindow?.postMessage(
        JSON.stringify({
          event: "command",
          func,
          args: [],
        }),
        "*"
      );
    } catch {
      // ignore
    }
  }

  private postToFeaturedVimeoPlayer(method: string, value?: any) {
    const iframe = this.featuredVideoIframe?.nativeElement;
    if (!iframe) return;

    try {
      iframe.contentWindow?.postMessage(
        JSON.stringify({
          method,
          value,
        }),
        "*"
      );
    } catch {
      // ignore
    }
  }

  private buildYoutubeEmbedUrl(videoId: string): SafeResourceUrl {
    const id = encodeURIComponent(videoId);
    const origin = typeof window !== "undefined" ? encodeURIComponent(window.location.origin) : "";
    const url =
      "https://www.youtube.com/embed/" +
      id +
      "?" +
      [
        // Required to control pause/play via postMessage.
        "enablejsapi=1",
        ...(origin ? ["origin=" + origin] : []),
        "autoplay=1",
        // Autoplay is most reliable when muted.
        "mute=1",
        "controls=0",
        // Don't load captions by default (users can still enable manually if desired).
        "cc_load_policy=0",
        "modestbranding=1",
        "rel=0",
        "playsinline=1",
        "iv_load_policy=3",
        "fs=0",
        "disablekb=1",
        "showinfo=0",
        // Loop the same video for a "hero background" feel.
        "loop=1",
        "playlist=" + id,
      ].join("&");

    return this._sanitizer.bypassSecurityTrustResourceUrl(url);
  }
}
